import React from "react";
import PropTypes from "prop-types";
import { Tooltip, Whisper } from "rsuite";
import StatusToggle from "../../components/common/StatusToggle";
import SummaryTooltipCell from "../../components/common/SummaryTooltipCell";
import PromoRowActions from "./PromoRowActions";
import { dateFormatter } from "../../utils/dateUtils";
import { P, usePermission } from "../../rbac";
import smartIcon from "../../assets/img/table-stars.svg";
import {
  isCampaignPromo,
  isGcRateplanPromo,
  isOmnibeesExternalPromo,
} from "./utils";
import {
  getDateRangeAsString,
  getStayDateRangeLabels,
  isSmartPromo,
  renderDiscount,
  formatDeletedOn,
} from "./promotionsTableUtils";

/**
 * Every badge this row earns, in render order.
 *
 * The order IS the layout: badges[0] sits on the name's line and the rest stack beneath it, so the
 * first entry is the one a hotelier sees without looking down. Authorship (Omnibees / Draft /
 * Recommended / GC) comes first because it says what the promo IS; the expiry pill follows because
 * it says what is happening to it.
 *
 * Omnibees and Draft return alone -- they are mutually exclusive with Recommended and GC and with
 * each other. The expiry pill is appended OUTSIDE that branch on purpose: expiry is orthogonal to
 * authorship, and an Omnibees promo expiring in 3 days must still say so.
 *
 * ⚠ expiryStatus is the WHOLE rule for the pill. No status check, no revenue check -- the backend
 * nulls the field for anything the hotelier cannot act on (not ACTIVE, CRS, buyer campaign, or
 * earned nothing). Re-adding a condition here does not make the table safer, it makes the two
 * surfaces disagree about the same promo with neither one owning the answer.
 */
const rowBadges = (promo, isPilotHotel, selectedProperty) => {
  const badges = [];

  if (isOmnibeesExternalPromo(promo, selectedProperty)) {
    badges.push(
      <span key="omnibees" className="external-badge">
        Omnibees
      </span>
    );
  } else if (promo.status === "Draft") {
    badges.push(
      <span key="draft" className="draft-badge">
        Draft
      </span>
    );
  } else {
    if (isSmartPromo(promo)) {
      badges.push(
        <span key="smart" className="smart-badge">
          <img
            src={smartIcon}
            alt={
              isPilotHotel ? "Recommended Derived Rate" : "Recommended Promo"
            }
            width="12"
            height="12"
          />
          &nbsp;Recommended
        </span>
      );
    }
    if (promo.isGcCreated) {
      badges.push(
        <span key="gc" className="gc-badge">
          GC
        </span>
      );
    }
  }

  if (promo.expiryStatus === "EXPIRING_SOON") {
    badges.push(
      <span key="expiring-soon" className="expiring-soon-badge">
        Expiring Soon
      </span>
    );
  }
  if (promo.expiryStatus === "EXPIRED") {
    badges.push(
      <span key="expired" className="expired-badge">
        Expired
      </span>
    );
  }

  return badges;
};

const PromotionRow = ({
  promo,
  index,
  selectedProperty,
  isPilotHotel,
  isDeletedView,
  onEdit,
  onView,
  onStatusToggle,
  onDelete,
}) => {
  const { allowed: canManage } = usePermission(P.Promotions.Manage);
  const isCampaign = isCampaignPromo(promo);
  const badges = rowBadges(promo, isPilotHotel, selectedProperty);

  const handleNameKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit(promo);
    }
  };

  return (
    <tr>
      <td className="sticky-col">
        <div className="promo-name-cell">
          <span className="promo-name-text">
            {/*
              Two explicit rows, not one wrapping row.

              ⚠ Wrapping was the previous attempt and it moved the wrong element.
              Flex wraps on DOM order, so once the badges filled the line the NAME was
              what dropped, landing on line two beside the badge that came after it.
              The name is how a hotelier identifies the row, so it stays on top with
              the first badge and ellipses; the badges are what can afford to sit a
              line lower.
            */}
            <span className="promo-name-line">
              {badges[0]}
              <span className="promo-name-slot">
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={
                    <Tooltip className="promo-name-tooltip">
                      {promo.name}
                    </Tooltip>
                  }
                >
                  {/* EO-1538: Campaign promos open too — view-only, like every other
                      promo a user cannot edit (openEditModal forces it). Only the
                      deleted view stays inert. */}
                  {isDeletedView ? (
                    <span className="promo-name-static">{promo.name}</span>
                  ) : (
                    <span
                      className="promo-name-label"
                      role="button"
                      tabIndex={0}
                      // Opens the editor; openEditModal makes it read-only for a
                      // Campaign, a GC rate-plan or Omnibees promo, or a user
                      // without promo.manage.
                      onClick={() => onEdit(promo)}
                      onKeyDown={handleNameKeyDown}
                    >
                      {promo.name}
                    </span>
                  )}
                </Whisper>
              </span>
            </span>
            {badges.length > 1 && (
              <span className="promo-name-badge-row">{badges.slice(1)}</span>
            )}
          </span>
          <PromoRowActions
            promo={promo}
            isViewable={
              !canManage ||
              isCampaign ||
              isGcRateplanPromo(promo) ||
              isOmnibeesExternalPromo(promo, selectedProperty)
            }
            isEditable={
              canManage &&
              !isGcRateplanPromo(promo) &&
              !isOmnibeesExternalPromo(promo, selectedProperty)
            }
            isDeleted={isDeletedView}
            onEdit={onEdit}
            onView={onView}
            onDelete={onDelete}
          />
        </div>
      </td>
      <td className="adjustment-cell">
        {renderDiscount({
          promo,
          index,
          isPilotHotel,
          selectedProperty,
        })}
      </td>
      <td>
        {canManage && !isDeletedView ? (
          <StatusToggle
            checked={promo.status === "Active"}
            onChange={() => onStatusToggle(promo)}
            disabled={
              isGcRateplanPromo(promo) ||
              (isOmnibeesExternalPromo(promo, selectedProperty) &&
                promo.status !== "Active")
            }
          />
        ) : (
          promo.status
        )}
      </td>
      {/* EO-1264: optional chaining on the range objects themselves, not just
          their fields. The deleted view renders this same row against deleted
          promos, whose payload can be sparse — see formatDeletedOn, where a
          missing value is already the expected case. A null bookingWindow or
          stayDates threw here and took the whole grid down, not just the cell.
          getDateRangeAsString already renders "N/A" when both ends are absent. */}
      <td>
        {getDateRangeAsString(
          promo.bookingWindow?.start,
          promo.bookingWindow?.end
        )}
      </td>
      {/* EO-1492: all stay ranges, first + "+N" tooltip — same affordance as the
          rate-plans cell. Falls back to the legacy single pair for old payloads. */}
      <td className="stay-dates-cell">
        <SummaryTooltipCell
          items={getStayDateRangeLabels(promo)}
          emptyText="N/A"
        />
      </td>
      <td className="rate-plans-cell">
        <SummaryTooltipCell items={promo.ratePlans} emptyText="N/A" />
      </td>
      <td className="exclusive-clients-cell">
        <SummaryTooltipCell items={promo.exclusiveClients} emptyText="N/A" />
      </td>
      {/* EO-1264: same reason as the ranges above — a deleted promo with no
          created timestamp threw on .split and took the grid down. */}
      <td>
        {promo.created ? dateFormatter(promo.created.split("T")[0]) : "N/A"}
      </td>
      <td>${promo.revenue || 0}</td>
      {isDeletedView && (
        <>
          <td>{formatDeletedOn(promo?.deletedAt)}</td>
          <td>{promo?.deletedBy ?? "N/A"}</td>
        </>
      )}
    </tr>
  );
};

PromotionRow.propTypes = {
  promo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    status: PropTypes.string,
    htPromo: PropTypes.bool,
    isGcCreated: PropTypes.bool,
    expiryStatus: PropTypes.string,
    source: PropTypes.string,
    promoType: PropTypes.string,
    bookingWindow: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
    stayDates: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
    stayDateRanges: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
      })
    ),
    ratePlans: PropTypes.array,
    exclusiveClients: PropTypes.array,
    created: PropTypes.string,
    revenue: PropTypes.number,
    deletedAt: PropTypes.string,
    deletedBy: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.string,
    acceptedCurrencies: PropTypes.arrayOf(PropTypes.string),
    crsId: PropTypes.number,
  }),
  isPilotHotel: PropTypes.bool,
  isDeletedView: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onStatusToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default PromotionRow;
